import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import cookie from "react-cookies";
import { getAcademyDetail } from "../../services/academyApi";
import { QtBackLink } from "../../components/icons/QtIcon";
import { AcademySlugImage, getAcademyMeta } from "../../components/icons/academyMeta";
import { displayProgramTitle, TRAINING, buildAcademyBreadcrumbs } from "./trainingTerms";
import {
  TrainingHero,
  TrainingLoading,
  TrainingError,
  TrainingModuleCard,
  TrainingProgressBar,
  TrainingSignInBanner,
  findNextModule,
  TrainingProgramOverview,
  CertAvailabilityNote,
  TrainingCertLadder,
} from "./AcademyShared";

export default function AcademyDetail({ Layout }) {
  const { academySlug } = useParams();
  const baseUrl = `/academy/${academySlug}`;
  const isLoggedIn = !!cookie.load("candidateKey");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const meta = getAcademyMeta(academySlug);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getAcademyDetail(academySlug)
      .then((result) => {
        if (!cancelled) {
          setData(result);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setData(null);
          setError(true);
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [academySlug]);

  if (loading) {
    return (
      <Layout title={TRAINING.program}>
        <TrainingLoading label={`Loading ${TRAINING.program.toLowerCase()}...`} />
      </Layout>
    );
  }

  if (error || !data) {
    return (
      <Layout title={TRAINING.program}>
        <TrainingError
          title={`Could not load this ${TRAINING.program.toLowerCase()}`}
          message="Check your connection and try again."
          action={
            <QtBackLink to="/academy" className="training-btn-secondary" style={{ marginTop: 12 }}>
              Back to all {TRAINING.programs.toLowerCase()}
            </QtBackLink>
          }
        />
      </Layout>
    );
  }

  const { academy, pillars = [], certifications = [] } = data;
  const allModules = pillars.flatMap((p) => p.modules || []);
  const done = allModules.filter((m) => m.status === "completed").length;
  const pct = allModules.length ? Math.round((done / allModules.length) * 100) : 0;
  const next = findNextModule([{ slug: academy.slug, modules: allModules }]);

  const moduleLink = (mod) => {
    const path = `${baseUrl}/module/${mod.slug}`;
    return isLoggedIn ? path : `/login?next=${encodeURIComponent(path)}`;
  };

  return (
    <Layout
      title={displayProgramTitle(academy.title)}
      titleChip="program"
      breadcrumbs={buildAcademyBreadcrumbs({
        programTitle: displayProgramTitle(academy.title),
        programSlug: academy.slug,
      })}
    >
      <div className={`training-academy-hero training-academy-hero--${academy.slug}`}>
        <div className="training-academy-hero__media">
          <AcademySlugImage slug={academy.slug} className="training-academy-hero__photo" loading="eager" />
        </div>
        <TrainingHero
          eyebrow={academy.kicker || meta.label}
          title={displayProgramTitle(academy.title)}
          subtitle={academy.purpose}
          typeChip="program"
        />
      </div>

      {isLoggedIn && allModules.length > 0 && (
        <TrainingProgressBar value={pct} label={`${displayProgramTitle(academy.title)} progress`} />
      )}

      {!isLoggedIn && <TrainingSignInBanner nextPath={baseUrl} />}

      <TrainingProgramOverview academy={academy} />

      {pillars.map((pillar) => (
        <div key={pillar.slug} className="training-card">
          <div className="training-card-head">
            <div>
              <h3 className="training-program-title">{pillar.title}</h3>
              {pillar.description && (
                <p className="training-subtitle" style={{ marginBottom: 0, fontSize: 15 }}>
                  {pillar.description}
                </p>
              )}
            </div>
          </div>
          {(pillar.modules || []).length === 0 ? (
            <p className="training-empty" style={{ padding: "16px 0" }}>
              {TRAINING.modules} coming soon.
            </p>
          ) : (
            (pillar.modules || []).map((mod) => (
              <TrainingModuleCard
                key={mod.slug}
                mod={mod}
                to={moduleLink(mod)}
                isCurrent={next?.mod?.slug === mod.slug}
              />
            ))
          )}
        </div>
      ))}

      {certifications.length > 0 && (
        <div className="training-card">
          <h3 className="training-program-title">Certification ladder</h3>
          <CertAvailabilityNote className="training-cert-availability--compact" />
          <TrainingCertLadder
            certifications={certifications}
            showBlockers={isLoggedIn}
            showDetailLinks={isLoggedIn}
            issuedLink={isLoggedIn ? "/me/certificates" : null}
          />
        </div>
      )}

      <QtBackLink to="/academy" className="training-btn-secondary" style={{ marginTop: 8 }}>
        All {TRAINING.programs.toLowerCase()}
      </QtBackLink>
    </Layout>
  );
}
